/**
 * InsuranceClaimPrint — print-friendly Insurance Claim / Pre-Auth document.
 *
 * Opens in a fresh tab. Auto-fires `window.print()` 800ms after render so the user
 * gets the print dialog without an extra click. Designed for A4 paper, monochrome-safe,
 * minimal CSS dependencies (Tailwind print utilities).
 *
 * Route: /insurance/print/{claim_id}
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { insuranceAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Loader2, Printer, ArrowLeft, ShieldCheck } from 'lucide-react';

const STATUS_COLORS = {
  Submitted: '#0ea5e9',
  Queried: '#f59e0b',
  Approved: '#10b981',
  Rejected: '#ef4444',
  Paid: '#059669',
};

const fmtINR = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const num = Number(v);
  if (Number.isNaN(num)) return v;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const InsuranceClaimPrint = () => {
  const { id } = useParams();
  const { hospital: authHospital } = useAuth();
  const [claim, setClaim] = useState(null);
  const [claimHospital, setClaimHospital] = useState(null);  // tenant-of-claim, resolved server-side
  const [loading, setLoading] = useState(true);
  const [autoPrinted, setAutoPrinted] = useState(false);

  useEffect(() => {
    let mounted = true;
    insuranceAPI.list()
      .then(async res => {
        if (!mounted) return;
        const found = (res.data || []).find(c => c.id === id);
        setClaim(found || null);
        // v37 fix — print should ALWAYS show the claim's tenant branding (not the printer's default).
        // For super-admin operators this matters: their auth.hospital is empty/platform-level.
        if (found?.hospital_id && found.hospital_id !== authHospital?.id) {
          try {
            const h = await api.get(`/hospitals/${found.hospital_id}`);
            if (mounted) setClaimHospital(h.data);
          } catch { /* fall back to authHospital */ }
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id, authHospital?.id]);

  // Effective hospital for header — prefer the claim's own tenant if resolved
  const hospital = claimHospital || authHospital || {};

  useEffect(() => {
    if (!loading && claim && !autoPrinted) {
      const t = setTimeout(() => { window.print(); setAutoPrinted(true); }, 800);
      return () => clearTimeout(t);
    }
  }, [loading, claim, autoPrinted]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 gap-2">
        <Loader2 className="animate-spin" size={20} /> Loading claim…
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Claim not found or access denied.
      </div>
    );
  }

  const statusColor = STATUS_COLORS[claim.status] || '#64748b';
  const hospitalName = hospital?.name || 'Klick Niramaya HMS';
  const hospitalAddress = hospital?.address || '';
  const hospitalPhone = hospital?.phone || '';
  const hospitalEmail = hospital?.email || hospital?.support_email || '';
  const branding = hospital?.branding || {};
  const primary = branding.primary_color || '#059669';

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Toolbar (hidden when printing) */}
      <div className="print:hidden bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => window.close()} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5" data-testid="print-back-btn">
          <ArrowLeft size={12} /> Close
        </button>
        <h1 className="text-sm font-semibold text-slate-700 hidden md:block">Insurance Claim · Print Preview</h1>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-md text-white"
          style={{ background: primary }}
          data-testid="print-trigger-btn">
          <Printer size={12} /> Print
        </button>
      </div>

      {/* Printable document */}
      <div className="max-w-3xl mx-auto bg-white p-10 print:p-8 my-6 print:my-0 print:shadow-none shadow-sm border border-slate-200 print:border-0" data-testid="insurance-print-doc">
        {/* Header */}
        <div className="flex items-start justify-between border-b-2 pb-5 mb-6" style={{ borderColor: primary }}>
          <div className="flex items-start gap-3">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={hospitalName} className="h-14 w-14 object-contain" />
            ) : (
              <div className="h-14 w-14 rounded-lg flex items-center justify-center text-white font-bold text-xl" style={{ background: primary }}>
                {hospitalName.charAt(0)}
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900">{hospitalName}</h1>
              {hospitalAddress && <p className="text-[10px] text-slate-500 mt-0.5 max-w-xs leading-relaxed">{hospitalAddress}</p>}
              <p className="text-[10px] text-slate-500 mt-0.5">
                {hospitalPhone && <>📞 {hospitalPhone}</>}
                {hospitalPhone && hospitalEmail && ' · '}
                {hospitalEmail && <>✉ {hospitalEmail}</>}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-white" style={{ background: statusColor }}>
              <ShieldCheck size={11} /> {claim.status || 'Submitted'}
            </div>
            <p className="mt-3 text-[10px] text-slate-500">Claim ID</p>
            <p className="text-sm font-mono font-bold text-slate-900">{claim.id?.slice(0, 8).toUpperCase() || '—'}</p>
            <p className="text-[10px] text-slate-500 mt-1">{fmtDate(claim.submitted_on || claim.created_at)}</p>
          </div>
        </div>

        <h2 className="text-base font-bold text-slate-800 mb-1">Insurance Claim / Pre-Authorisation</h2>
        <p className="text-[11px] text-slate-500 mb-5">{claim.claim_type || 'Cashless Pre-Auth'} request to TPA / Insurer.</p>

        {/* Patient Block */}
        <section className="mb-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b">Patient Details</h3>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <Field label="Patient Name" value={claim.patient_name} />
            <Field label="UHID" value={claim.patient_uhid} mono />
          </div>
        </section>

        {/* Insurer Block */}
        <section className="mb-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b">Insurer / TPA</h3>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <Field label="Insurer / TPA" value={claim.insurer} />
            <Field label="Policy Number" value={claim.policy_no} mono />
            <Field label="Claim Type" value={claim.claim_type} />
            <Field label="Submitted On" value={fmtDate(claim.submitted_on || claim.created_at)} />
          </div>
        </section>

        {/* Financials */}
        <section className="mb-5">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b">Financial Summary</h3>
          <table className="w-full text-xs border border-slate-200 rounded">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-600 w-1/2">Total Claim Amount</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{fmtINR(claim.claim_amount)}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-slate-600">Approved Amount</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">{fmtINR(claim.approved_amount)}</td>
              </tr>
              {claim.claim_amount && claim.approved_amount != null && (
                <tr className="bg-slate-50">
                  <td className="px-3 py-2 text-slate-700 font-semibold">Balance / Co-Pay</td>
                  <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: primary }}>
                    {fmtINR(Math.max(0, Number(claim.claim_amount) - Number(claim.approved_amount || 0)))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* Notes */}
        {claim.notes && (
          <section className="mb-5">
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b">Notes</h3>
            <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{claim.notes}</p>
          </section>
        )}

        {/* Signature block */}
        <div className="grid grid-cols-2 gap-12 mt-12 pt-8 border-t border-slate-200">
          <div className="text-center">
            <div className="h-12 border-b border-slate-400 mb-2" />
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Authorised Signatory</p>
            <p className="text-[9px] text-slate-400">{hospitalName}</p>
          </div>
          <div className="text-center">
            <div className="h-12 border-b border-slate-400 mb-2" />
            <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Patient / Guardian Signature</p>
            <p className="text-[9px] text-slate-400">{claim.patient_name || '—'}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 pt-4 border-t border-slate-100 text-center">
          <p className="text-[9px] text-slate-400">
            This is a system-generated document from {hospitalName}.
            {claim.id && <> · Document ref: INS-{claim.id?.slice(0, 8).toUpperCase()}</>}
          </p>
          <p className="text-[9px] text-slate-400 mt-0.5">Powered by Klick Niramaya HMS · Printed on {new Date().toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Print-specific overrides */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className={`text-xs text-slate-800 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

export default InsuranceClaimPrint;
